// Pure Helfer für die Druckliste (getestet). Größen-Sortierung spiegelt backend/src/groessen.ts.
import type { Auftrag, Kontrollsumme, Position } from './typen';

const BUCHSTABEN = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL', '6XL'];

export function normalisiereGroesse(roh: string | null | undefined): string {
  const s = String(roh ?? '').trim().toUpperCase().replace(/\s+/g, '');
  if (!s) return 'OHNE';
  if (s === '2XL') return 'XXL';
  if (s === 'XXXL') return '3XL';
  if (s === 'XXXXL') return '4XL';
  return s;
}

function rang(g: string): [number, number, string] {
  if (/^\d+$/.test(g)) return Number(g) >= 80 ? [0, Number(g), g] : [2, Number(g), g];
  const i = BUCHSTABEN.indexOf(g);
  if (i >= 0) return [1, i, g];
  return g === 'OHNE' ? [4, 0, g] : [3, 0, g];
}

export function vergleicheGroessen(a: string, b: string): number {
  const ra = rang(normalisiereGroesse(a));
  const rb = rang(normalisiereGroesse(b));
  return ra[0] - rb[0] || ra[1] - rb[1] || ra[2].localeCompare(rb[2]);
}

export function initialenAusName(vorname: string, nachname: string): string {
  const erster = (s: string) => (s.trim().match(/\p{L}/u)?.[0] ?? '').toUpperCase();
  return erster(vorname) + erster(nachname);
}

export const INITIALEN_MIN = 2;
export const INITIALEN_MAX = 3;

/** Wie backend/src/liste.ts: nur Buchstaben/Ziffern, Großschrift, max. 3 Zeichen. */
export function bereinigeInitialen(roh: string): string {
  return String(roh ?? '').toUpperCase().replace(/[^\p{L}\p{N}]/gu, '').slice(0, INITIALEN_MAX);
}

/** Positionen mit Initialen, aber weniger als 2 Zeichen (z. B. nur Vorname bekannt). */
export function zuKurzeInitialen(positionen: Position[]): Position[] {
  return positionen.filter((p) => p.initialen && p.initialen.length < INITIALEN_MIN);
}

export function leerePosition(nr: number): Position {
  return { nr, vorname: '', nachname: '', initialen: '', groesse: '', menge: 1, unsicher: false, anmerkung: '', erledigt: false };
}

export function nummeriere(positionen: Position[]): Position[] {
  return positionen.map((p, i) => ({ ...p, nr: i + 1 }));
}

export function zaehleJeGroesse(positionen: Position[]): Kontrollsumme[] {
  const m = new Map<string, number>();
  for (const p of positionen) {
    if (!p.initialen && !p.vorname && !p.nachname) continue;
    const g = normalisiereGroesse(p.groesse);
    m.set(g, (m.get(g) ?? 0) + (p.menge || 1));
  }
  return [...m.keys()].sort(vergleicheGroessen).map((g) => ({ groesse: g, anzahl: m.get(g)! }));
}

export function abweichungen(positionen: Position[], kunde: Kontrollsumme[]) {
  if (!kunde.length) return [];
  const liste = new Map(zaehleJeGroesse(positionen).map((k) => [k.groesse, k.anzahl]));
  const soll = new Map<string, number>();
  for (const k of kunde) soll.set(normalisiereGroesse(k.groesse), (soll.get(normalisiereGroesse(k.groesse)) ?? 0) + k.anzahl);
  return [...new Set([...liste.keys(), ...soll.keys()])]
    .sort(vergleicheGroessen)
    .map((g) => ({ groesse: g, liste: liste.get(g) ?? 0, kunde: soll.get(g) ?? 0 }))
    .filter((d) => d.liste !== d.kunde);
}

/** Positionen ohne Initialen — die fehlen sonst still auf dem Bogen. */
export function ohneInitialen(positionen: Position[]): Position[] {
  return positionen.filter((p) => !p.initialen.trim() && (p.vorname || p.nachname));
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Druckansicht wie die Papierliste der Druckabteilung: nach Größe gruppiert, mit Abhak-Kästchen. */
export function druckansichtHtml(a: Auftrag, datum = new Date()): string {
  const gruppen = new Map<string, Position[]>();
  for (const p of a.positionen) {
    if (!p.initialen && !p.vorname && !p.nachname) continue;
    const g = normalisiereGroesse(p.groesse);
    if (!gruppen.has(g)) gruppen.set(g, []);
    gruppen.get(g)!.push(p);
  }
  const summe = zaehleJeGroesse(a.positionen);
  const gesamt = summe.reduce((s, k) => s + k.anzahl, 0);
  const zeilen = [...gruppen.keys()].sort(vergleicheGroessen).map((g) => {
    const ps = gruppen.get(g)!;
    const n = ps.reduce((s, p) => s + (p.menge || 1), 0);
    return `<tr class="gr"><td colspan="6">Größe ${esc(g === 'OHNE' ? '–' : g)} · ${n} Stk</td></tr>` +
      ps.map((p) => `<tr><td>${p.nr}</td><td>${esc(`${p.vorname} ${p.nachname}`.trim())}</td>` +
        `<td><span class="ini">${esc(p.initialen)}</span></td><td><span class="gb">${esc(g === 'OHNE' ? '–' : g)}</span></td>` +
        `<td>${p.menge > 1 ? `${p.menge}×` : ''} ${esc(p.anmerkung)}</td><td class="cb">${p.erledigt ? '☑' : '☐'}</td></tr>`).join('');
  }).join('');
  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(a.titel)}</title><style>
body{font-family:Arial,Helvetica,sans-serif;color:#1A1A1A;margin:24px;font-size:12px}
.kopf{background:#1A1A1A;color:#fff;padding:12px 16px;display:flex;justify-content:space-between;align-items:center}
.kopf h1{font-size:18px;margin:0}.kopf span{background:#E9FF57;color:#1A1A1A;padding:3px 8px;border-radius:4px;font-weight:bold}
.meta{display:flex;gap:32px;margin:12px 0}.meta div b{display:block;font-size:10px;color:#666}
.summe span{display:inline-block;margin:0 6px 4px 0;padding:2px 6px;border:1px solid #DAD9D9;border-radius:4px}
table{width:100%;border-collapse:collapse;margin-top:12px}th{background:#1A1A1A;color:#fff;text-align:left;padding:6px}
td{padding:5px 6px;border-bottom:1px solid #DAD9D9}tr.gr td{background:#eef1f4;font-weight:bold;font-size:11px}
.ini{background:#1A1A1A;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold}.gb{background:#1e3a8a;color:#fff;padding:2px 6px;border-radius:3px}
.cb{font-size:16px;text-align:center}.hinw{margin-top:8px;padding:8px;border-left:4px solid #E9FF57;background:#fafafa;white-space:pre-wrap}
@media print{body{margin:0}.kopf,th,.ini,.gb,tr.gr td{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="kopf"><h1>${esc(a.titel)}</h1><span>${gesamt} Stk</span></div>
<div class="meta"><div><b>Artikel</b>${esc(a.artikel || '–')}</div><div><b>Datum</b>${datum.toLocaleDateString('de-DE')}</div><div><b>Positionen</b>${a.positionen.length}</div></div>
<div class="summe">${summe.map((k) => `<span>${esc(k.groesse === 'OHNE' ? 'ohne Gr.' : k.groesse)}: <b>${k.anzahl}</b></span>`).join('')}</div>
${a.hinweise ? `<div class="hinw">${esc(a.hinweise)}</div>` : ''}
<table><thead><tr><th>#</th><th>Name</th><th>Initialen</th><th>Größe</th><th>Hinweis</th><th>Erledigt</th></tr></thead><tbody>${zeilen}</tbody></table>
</body></html>`;
}
